/**
 * VÉRAULT Travels - Trip Planning Context
 * 
 * SINGLE SOURCE OF TRUTH for trip dates, days, and planning state.
 * Used by AI Assistant, Itinerary Builder, and all advisor tabs.
 * 
 * Key Features:
 * 1. Trip dates as master source
 * 2. Auto-calculated total days
 * 3. Day navigation with deep linking
 * 4. Day lock/unlock state
 * 5. Bi-directional sync between AI and Itinerary
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TripPlanningContext = createContext();

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Parse a date string as a local date (not UTC)
 * This fixes timezone offset issues where "2026-06-02" becomes June 1 in US timezones
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const parts = dateStr.split('T')[0].split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  return new Date(dateStr);
}

/**
 * Calculate total trip days from start and end dates
 */
export function calculateTripDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  try {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    if (!start || !end) return 0;
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end day
    return diffDays;
  } catch {
    return 0;
  }
}

/**
 * Get the calendar date for a specific day number
 */
export function getDayDate(startDate, dayNumber) {
  if (!startDate || !dayNumber) return null;
  try {
    const date = parseLocalDate(startDate);
    if (!date) return null;
    date.setDate(date.getDate() + (dayNumber - 1));
    return date;
  } catch {
    return null;
  }
}

/**
 * Format day header: "Day 1 · Mon, Feb 15"
 */
export function formatDayHeader(startDate, dayNumber) {
  const date = getDayDate(startDate, dayNumber);
  if (!date) return `Day ${dayNumber}`;
  const formatted = date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
  return `Day ${dayNumber} · ${formatted}`;
}

/**
 * Format short day label: "D1 · Feb 15"
 */
export function formatDayShort(startDate, dayNumber) {
  const date = getDayDate(startDate, dayNumber);
  if (!date) return `D${dayNumber}`;
  const formatted = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
  return `D${dayNumber} · ${formatted}`;
}

/**
 * Get ISO date string for a day number
 */
export function getDayISODate(startDate, dayNumber) {
  const date = getDayDate(startDate, dayNumber);
  if (!date) return null;
  return date.toISOString().split('T')[0];
}

// ============================================================
// PROVIDER COMPONENT
// ============================================================

export function TripPlanningProvider({ children, tripId, startDate, endDate }) {
  // Core planning state
  const [currentDay, setCurrentDay] = useState(1);
  const [lockedDays, setLockedDays] = useState({});
  const [dayStatuses, setDayStatuses] = useState({}); // draft, proposed, approved, booked
  const [itineraryItems, setItineraryItems] = useState([]); // Items from itinerary
  const [aiProposals, setAiProposals] = useState([]); // Pending AI proposals
  const [syncVersion, setSyncVersion] = useState(0); // Increment to trigger re-fetches
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Calculate total days (memoized)
  const totalDays = useMemo(() => {
    return calculateTripDays(startDate, endDate);
  }, [startDate, endDate]);
  
  // Generate days array for navigation
  const daysArray = useMemo(() => {
    if (!totalDays) return [];
    return Array.from({ length: totalDays }, (_, i) => ({
      dayNumber: i + 1,
      date: getDayDate(startDate, i + 1),
      dateString: getDayISODate(startDate, i + 1),
      label: formatDayShort(startDate, i + 1),
      fullLabel: formatDayHeader(startDate, i + 1),
      isLocked: lockedDays[i + 1] || false,
      status: dayStatuses[i + 1] || 'draft',
      itemCount: (itineraryItems || []).filter(item => item.day_number === i + 1).length,
      proposalCount: (aiProposals || []).filter(p => p.day_number === i + 1 && !p.applied && !p.rejected).length,
    }));
  }, [totalDays, startDate, lockedDays, dayStatuses, itineraryItems, aiProposals]);
  
  // Current day info
  const currentDayInfo = useMemo(() => {
    return daysArray.find(d => d.dayNumber === currentDay) || null;
  }, [daysArray, currentDay]);
  
  // ============================================================
  // DATA FETCHING
  // ============================================================
  
  // Load planning state from backend
  const loadPlanningState = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-planning-state`);
      if (res.data) {
        if (res.data.locked_days) setLockedDays(res.data.locked_days);
        if (res.data.day_statuses) setDayStatuses(res.data.day_statuses);
        if (res.data.current_working_day) setCurrentDay(res.data.current_working_day);
      }
    } catch (err) {
      console.error('Failed to load planning state:', err);
    } finally {
      setLoading(false);
    }
  }, [tripId]);
  
  // Load itinerary items
  const loadItineraryItems = useCallback(async () => {
    if (!tripId) return;
    try {
      const res = await axios.get(`${API}/travels/trip-itinerary/${tripId}`);
      // Handle both array of days and items format
      const data = res.data || [];
      if (Array.isArray(data)) {
        // Flatten all items from all days into a single array
        const items = [];
        data.forEach((day, idx) => {
          const dayNumber = day.day_number || idx + 1;
          // Collect items from all time blocks
          ['morning', 'afternoon', 'evening', 'late_night', 'all_day'].forEach(block => {
            if (Array.isArray(day[block])) {
              day[block].forEach(item => {
                items.push({ ...item, day_number: dayNumber });
              });
            }
          });
          // Collect meal items
          ['breakfast', 'lunch', 'dinner'].forEach(meal => {
            if (Array.isArray(day[meal])) {
              day[meal].forEach(item => {
                items.push({ ...item, day_number: dayNumber });
              });
            }
          });
        });
        setItineraryItems(items);
      } else {
        setItineraryItems(data?.items || []);
      }
    } catch (err) {
      console.error('Failed to load itinerary:', err);
      setItineraryItems([]);
    }
  }, [tripId]);
  
  // Load AI proposals
  const loadAiProposals = useCallback(async () => {
    if (!tripId) return;
    try {
      const res = await axios.get(`${API}/travels/trips/${tripId}/ai-proposals`);
      // Handle both { proposals: [...] } and direct array response
      const proposals = res.data?.proposals || (Array.isArray(res.data) ? res.data : []);
      setAiProposals(proposals);
    } catch (err) {
      console.error('Failed to load AI proposals:', err);
      setAiProposals([]);
    }
  }, [tripId]);
  
  // Initial load
  useEffect(() => {
    if (tripId) {
      loadPlanningState();
      loadItineraryItems();
      loadAiProposals();
    }
  }, [tripId, loadPlanningState, loadItineraryItems, loadAiProposals]);
  
  // Reload when sync version changes
  useEffect(() => {
    if (syncVersion > 0) {
      loadItineraryItems();
      loadAiProposals();
    }
  }, [syncVersion, loadItineraryItems, loadAiProposals]);
  
  // ============================================================
  // DAY NAVIGATION
  // ============================================================
  
  const goToDay = useCallback((dayNumber) => {
    if (dayNumber >= 1 && dayNumber <= totalDays) {
      setCurrentDay(dayNumber);
      // Persist to backend
      if (tripId) {
        axios.patch(`${API}/travels/trips/${tripId}/ai-planning-state`, {
          current_working_day: dayNumber
        }).catch(console.error);
      }
    }
  }, [totalDays, tripId]);
  
  const goToNextDay = useCallback(() => {
    if (currentDay < totalDays) {
      goToDay(currentDay + 1);
    }
  }, [currentDay, totalDays, goToDay]);
  
  const goToPrevDay = useCallback(() => {
    if (currentDay > 1) {
      goToDay(currentDay - 1);
    }
  }, [currentDay, goToDay]);
  
  // ============================================================
  // DAY LOCKING
  // ============================================================
  
  const lockDay = useCallback(async (dayNumber) => {
    if (!tripId) return;
    setLockedDays(prev => ({ ...prev, [dayNumber]: true }));
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-planning-state/lock-day/${dayNumber}`);
    } catch (err) {
      console.error('Failed to lock day:', err);
      setLockedDays(prev => ({ ...prev, [dayNumber]: false }));
    }
  }, [tripId]);
  
  const unlockDay = useCallback(async (dayNumber) => {
    if (!tripId) return;
    setLockedDays(prev => ({ ...prev, [dayNumber]: false }));
    try {
      await axios.post(`${API}/travels/trips/${tripId}/ai-planning-state/unlock-day/${dayNumber}`);
    } catch (err) {
      console.error('Failed to unlock day:', err);
      setLockedDays(prev => ({ ...prev, [dayNumber]: true }));
    }
  }, [tripId]);
  
  const toggleDayLock = useCallback((dayNumber) => {
    if (lockedDays[dayNumber]) {
      unlockDay(dayNumber);
    } else {
      lockDay(dayNumber);
    }
  }, [lockedDays, lockDay, unlockDay]);
  
  const isDayLocked = useCallback((dayNumber) => {
    return lockedDays[dayNumber] || false;
  }, [lockedDays]);
  
  // ============================================================
  // DAY STATUS
  // ============================================================
  
  const setDayStatus = useCallback(async (dayNumber, status) => {
    if (!tripId) return;
    setDayStatuses(prev => ({ ...prev, [dayNumber]: status }));
    try {
      await axios.patch(`${API}/travels/trips/${tripId}/ai-planning-state`, {
        day_statuses: { ...dayStatuses, [dayNumber]: status }
      });
    } catch (err) {
      console.error('Failed to update day status:', err);
    }
  }, [tripId, dayStatuses]);
  
  // ============================================================
  // SYNC FUNCTIONS
  // ============================================================
  
  /**
   * Trigger a sync refresh - call this after any itinerary or proposal changes
   */
  const triggerSync = useCallback(() => {
    setSyncVersion(v => v + 1);
  }, []);
  
  /**
   * Transfer AI proposals for a day to the itinerary
   */
  const transferDayToItinerary = useCallback(async (dayNumber, mode = 'append') => {
    if (!tripId) return { success: false, error: 'No trip ID' };
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/travels/trips/${tripId}/ai-proposals/apply-day`, {
        day_number: dayNumber,
        mode: mode
      });
      triggerSync();
      return { success: true, data: res.data };
    } catch (err) {
      console.error('Failed to transfer day:', err);
      return { success: false, error: err.message };
    } finally {
      setSyncing(false);
    }
  }, [tripId, triggerSync]);
  
  /**
   * Get items for a specific day
   */
  const getItemsForDay = useCallback((dayNumber) => {
    return (itineraryItems || []).filter(item => item.day_number === dayNumber);
  }, [itineraryItems]);
  
  /**
   * Get proposals for a specific day
   */
  const getProposalsForDay = useCallback((dayNumber) => {
    return (aiProposals || []).filter(p => p.day_number === dayNumber && !p.applied && !p.rejected);
  }, [aiProposals]);
  
  // ============================================================
  // CONTEXT VALUE
  // ============================================================
  
  const value = useMemo(() => ({
    // Trip info
    tripId,
    startDate,
    endDate,
    totalDays,
    
    // Days array
    daysArray,
    
    // Current day
    currentDay,
    currentDayInfo,
    goToDay,
    goToNextDay,
    goToPrevDay,
    
    // Locking
    lockedDays,
    lockDay,
    unlockDay,
    toggleDayLock,
    isDayLocked,
    
    // Status
    dayStatuses,
    setDayStatus,
    
    // Data
    itineraryItems,
    aiProposals,
    getItemsForDay,
    getProposalsForDay,
    
    // Sync
    syncVersion,
    triggerSync,
    transferDayToItinerary,
    
    // Loading
    loading,
    syncing,
    
    // Refresh
    refreshPlanningState: loadPlanningState,
    refreshItinerary: loadItineraryItems,
    refreshProposals: loadAiProposals,
    
    // Utilities
    formatDayHeader,
    formatDayShort,
    getDayDate,
    getDayISODate,
  }), [
    tripId, startDate, endDate, totalDays,
    daysArray, currentDay, currentDayInfo, goToDay, goToNextDay, goToPrevDay,
    lockedDays, lockDay, unlockDay, toggleDayLock, isDayLocked,
    dayStatuses, setDayStatus,
    itineraryItems, aiProposals, getItemsForDay, getProposalsForDay,
    syncVersion, triggerSync, transferDayToItinerary,
    loading, syncing,
    loadPlanningState, loadItineraryItems, loadAiProposals
  ]);
  
  return (
    <TripPlanningContext.Provider value={value}>
      {children}
    </TripPlanningContext.Provider>
  );
}

// ============================================================
// HOOK
// ============================================================

export function useTripPlanning() {
  const context = useContext(TripPlanningContext);
  if (!context) {
    throw new Error('useTripPlanning must be used within a TripPlanningProvider');
  }
  return context;
}

// ============================================================
// DAY NAVIGATOR COMPONENT (Reusable across tabs)
// ============================================================

export function DayNavigator({ 
  onDayChange, 
  showLockControls = true,
  showProposalCounts = true,
  compact = false,
  className = ''
}) {
  const { 
    daysArray, 
    currentDay, 
    totalDays,
    goToDay, 
    toggleDayLock,
    startDate
  } = useTripPlanning();
  
  const handleDayClick = (dayNumber) => {
    goToDay(dayNumber);
    onDayChange?.(dayNumber);
  };
  
  if (!totalDays) {
    return (
      <div className={`flex items-center justify-center p-4 bg-[#F9F8F4] rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}>
        <p className="text-sm text-[#8C8C8C]">Set trip dates to enable day planning</p>
      </div>
    );
  }
  
  return (
    <div className={`bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(0,0,0,0.06)]">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#1A1A1A]">Trip Days</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-[#C8A85A]/10 text-[#C8A85A]">
            {totalDays} days
          </span>
        </div>
        <div className="text-xs text-[#8C8C8C]">
          {startDate && (() => {
            const start = parseLocalDate(startDate);
            const endDate = new Date(start);
            endDate.setDate(start.getDate() + totalDays - 1);
            return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          })()}
        </div>
      </div>
      
      {/* Day Pills - Scrollable */}
      <div className="flex gap-2 p-3 overflow-x-auto scrollbar-hide">
        {daysArray.map((day) => (
          <button
            key={day.dayNumber}
            onClick={() => handleDayClick(day.dayNumber)}
            className={`flex-shrink-0 flex flex-col items-center justify-center rounded-lg border transition-all ${
              compact ? 'px-3 py-2 min-w-[60px]' : 'px-4 py-3 min-w-[80px]'
            } ${
              currentDay === day.dayNumber
                ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]'
                : day.isLocked
                  ? 'bg-[#F5F1EB] border-[#8DA399]/30 text-[#1A1A1A]'
                  : 'bg-white border-[rgba(0,0,0,0.06)] text-[#1A1A1A] hover:border-[#C8A85A]/50'
            }`}
          >
            <span className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
              D{day.dayNumber}
            </span>
            <span className={`text-[#8C8C8C] ${compact ? 'text-[10px]' : 'text-xs'} ${currentDay === day.dayNumber ? 'text-white/70' : ''}`}>
              {day.date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || 'TBD'}
            </span>
            
            {/* Indicators */}
            <div className="flex items-center gap-1 mt-1">
              {day.isLocked && (
                <span className={`w-1.5 h-1.5 rounded-full ${currentDay === day.dayNumber ? 'bg-white/70' : 'bg-[#8DA399]'}`} />
              )}
              {showProposalCounts && day.proposalCount > 0 && (
                <span className={`text-[10px] px-1.5 rounded-full ${
                  currentDay === day.dayNumber ? 'bg-white/20 text-white' : 'bg-[#C8A85A]/20 text-[#C8A85A]'
                }`}>
                  {day.proposalCount}
                </span>
              )}
              {day.itemCount > 0 && (
                <span className={`text-[10px] px-1.5 rounded-full ${
                  currentDay === day.dayNumber ? 'bg-white/20 text-white' : 'bg-[#8DA399]/20 text-[#8DA399]'
                }`}>
                  {day.itemCount}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
      
      {/* Lock controls for current day */}
      {showLockControls && currentDay && (
        <div className="px-4 py-2 border-t border-[rgba(0,0,0,0.06)] flex items-center justify-between bg-[#F9F8F4]">
          <span className="text-xs text-[#8C8C8C]">
            {daysArray.find(d => d.dayNumber === currentDay)?.fullLabel}
          </span>
          <button
            onClick={() => toggleDayLock(currentDay)}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
              daysArray.find(d => d.dayNumber === currentDay)?.isLocked
                ? 'bg-[#8DA399]/10 text-[#8DA399]'
                : 'bg-[rgba(0,0,0,0.05)] text-[#8C8C8C] hover:bg-[rgba(0,0,0,0.08)]'
            }`}
          >
            {daysArray.find(d => d.dayNumber === currentDay)?.isLocked ? (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Locked
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Lock Day
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TRIP DAYS SUMMARY COMPONENT
// ============================================================

export function TripDaysSummary({ className = '' }) {
  const { totalDays, daysArray, startDate, endDate } = useTripPlanning();
  
  const completedDays = daysArray.filter(d => d.status === 'approved' || d.status === 'booked').length;
  const daysWithContent = daysArray.filter(d => d.itemCount > 0).length;
  const lockedDaysCount = daysArray.filter(d => d.isLocked).length;
  
  if (!totalDays) {
    return (
      <div className={`text-sm text-[#8C8C8C] ${className}`}>
        Dates not set
      </div>
    );
  }
  
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="text-lg font-serif text-[#1A1A1A]">{totalDays} days</span>
        <span className="text-sm text-[#8C8C8C]">
          {parseLocalDate(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {parseLocalDate(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1 text-[#8DA399]">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {completedDays}/{totalDays} complete
        </span>
        <span className="flex items-center gap-1 text-[#C8A85A]">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
          </svg>
          {daysWithContent} planned
        </span>
        {lockedDaysCount > 0 && (
          <span className="flex items-center gap-1 text-[#595959]">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            {lockedDaysCount} locked
          </span>
        )}
      </div>
    </div>
  );
}

export default TripPlanningContext;
